import { useCallback, useRef, useState } from 'react';
import type { LimosaPdfGegevens, LimosaPdfLezing } from '@/api/administratie';

export type PdfState =
  | { kind: 'geen' }
  | { kind: 'foto' }
  | { kind: 'bezig' }
  | { kind: 'onleesbaar' }
  | { kind: 'gelezen'; lezing: Extract<LimosaPdfLezing, { leesbaar: true }> };

const isPdf = (file: File) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

/**
 * Read a chosen Limosa PDF on the server and hand its fields to the form. Only a
 * PDF is read (a photo stays manual). A late answer for a file that was replaced
 * in the meantime is dropped.
 */
export function useLimosaPdf(read: (file: File) => Promise<LimosaPdfLezing>, onGegevens: (g: LimosaPdfGegevens) => void) {
  const [state, setState] = useState<PdfState>({ kind: 'geen' });
  const seq = useRef(0);

  const choose = useCallback((file: File | null) => {
    const n = ++seq.current;
    if (!file) return setState({ kind: 'geen' });
    if (!isPdf(file)) return setState({ kind: 'foto' });
    setState({ kind: 'bezig' });
    read(file)
      .then((lezing) => {
        if (n !== seq.current) return;
        if (!lezing.leesbaar) return setState({ kind: 'onleesbaar' });
        onGegevens(lezing.gegevens);
        setState({ kind: 'gelezen', lezing });
      })
      .catch(() => {
        if (n === seq.current) setState({ kind: 'onleesbaar' });
      });
  }, [read, onGegevens]);

  const reset = useCallback(() => {
    seq.current++;
    setState({ kind: 'geen' });
  }, []);

  return { state, choose, reset };
}

/** One line under the file picker: reading, filled in from the PDF, or fill in yourself. */
export function LimosaPdfStatus({ state }: { state: PdfState }) {
  const text = {
    geen: null,
    bezig: 'PDF wordt gelezen…',
    foto: 'Vul de gegevens zelf in.',
    onleesbaar: 'We konden deze PDF niet lezen. Vul de gegevens zelf in.',
    gelezen: 'Ingevuld uit de PDF. Kijk de gegevens na voor je oplaadt.',
  }[state.kind];
  return text && <p className="text-xs text-td-muted" role="status">{text}</p>;
}

/**
 * What else the PDF told us, after the fields: the place(s) of employment, and an
 * orange warning when the PDF names someone else than `person`. Saving stays allowed.
 */
export function LimosaPdfDetails({ state, person }: { state: PdfState; person: { id: number; name: string } | undefined }) {
  if (state.kind !== 'gelezen') return null;
  const { gegevens, afwijking } = state.lezing;
  const af = person ? afwijking[String(person.id)] : undefined;
  const werkgever = gegevens.werkgever.naam ?? gegevens.werkgever.btw;
  return (
    <div className="space-y-1 text-xs">
      {gegevens.plaatsen.length > 0 && (
        <div className="text-td-muted">
          <p>Plaats van tewerkstelling volgens de PDF:</p>
          <ul className="list-disc pl-4" data-testid="limosa-pdf-plaatsen">
            {gegevens.plaatsen.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </div>
      )}
      {person && af && (af.naam || af.werkgever) && (
        <div className="rounded-lg border border-orange-300 bg-orange-50 p-2 text-orange-800" role="alert">
          {af.naam && <p>Let op: de naam op de PDF ({gegevens.werknemer.naam}) is niet {person.name}.</p>}
          {af.werkgever && <p>Let op: de werkgever op de PDF ({werkgever}) is niet het bedrijf van {person.name}.</p>}
          <p>Je kan toch opladen; de administratie ziet dit bij de controle.</p>
        </div>
      )}
    </div>
  );
}
